import React, { useCallback } from 'react';
import { getClientEnvironmentDetails } from '@sm/utils';

import useFeatureSetStyles from '~app/pages/Pricing/components/FeatureSet/useStyles';
import { DisplayFeatureStatic, SummaryDisplayFeatureList } from '~shared/types/pricing/features';
import FeatureTooltip from '~pricing/components/Tooltip';
import uniqueFeatureNameMap from './uniqueFeatureNameMap';
import { UniqueFeatureName } from './types';

function getUniqueFeatureName(featureName: string): string {
  const uniqueFeature = Object.keys(uniqueFeatureNameMap).find(feature => {
    return uniqueFeatureNameMap[feature as keyof UniqueFeatureName]?.includes(featureName) ? feature : null;
  });

  return uniqueFeature ?? featureName;
}

export default function FeatureSet({
  features,
  packageName,
  isLastPackage,
}: {
  features: SummaryDisplayFeatureList;
  packageName: string;
  isLastPackage: boolean;
}): React.ReactElement {
  const { Features, FeatureItem, FeatureSummary, TooltipContainer, Highlight } = useFeatureSetStyles();
  const { isMobile } = getClientEnvironmentDetails();

  const handleFeatureHover = useCallback(
    (featureName): void => {
      if (!isMobile) {
        document
          .querySelectorAll(`[data-feature='${featureName}']`)
          .forEach(feature => feature.classList.add(Highlight));
      }
    },
    [Highlight, isMobile]
  );

  const handleFeatureLeave = useCallback(
    (featureName): void => {
      if (!isMobile) {
        document
          .querySelectorAll(`[data-feature='${featureName}']`)
          .forEach(feature => feature.classList.remove(Highlight));
      }
    },
    [Highlight, isMobile]
  );

  return (
    <ul className={Features}>
      {features
        .filter((feature: DisplayFeatureStatic) => !!feature)
        .map((feature: DisplayFeatureStatic) => {
          const featureName = feature?.name && getUniqueFeatureName(feature?.name);
          return (
            <li
              className={FeatureItem}
              key={`${packageName}-feature-${feature?.summary}`}
              data-feature={featureName}
              onMouseEnter={handleFeatureHover.bind(null, featureName)}
              onMouseLeave={handleFeatureLeave.bind(null, featureName)}
            >
              {feature?.summary && (
                <span className={FeatureSummary}>
                  {feature?.summary}
                  {feature?.tooltip && feature?.tooltipHeading && (
                    <span className={TooltipContainer}>
                      <FeatureTooltip
                        tooltipHeading={feature.tooltipHeading}
                        tooltip={feature.tooltip}
                        isLastPackage={isLastPackage}
                      />
                    </span>
                  )}
                </span>
              )}
            </li>
          );
        })}
    </ul>
  );
}
